import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  Card,
  Typography,
  Button,
  Input,
  Form,
  Spin,
  Result,
  Space,
  message,
} from "antd";
import {
  DownloadOutlined,
  LockOutlined,
  FileOutlined,
} from "@ant-design/icons";
import { FileIcon } from "@/components/shared";
import { formatFileSize } from "@/utils/formatters";
import { getPublicShare, downloadPublicShare } from "@/services/storageManager";

const { Title, Text, Paragraph } = Typography;

interface ShareData {
  isValid: boolean;
  isExpired: boolean;
  requiresPassword: boolean;
  permissions?: "view" | "download";
  fileName?: string;
  mimeType?: string;
  size?: number;
}

function shareErrorCode(error: unknown): string | undefined {
  if (!axios.isAxiosError(error)) return undefined;
  const body = error.response?.data as
    | { code?: string; error?: string }
    | undefined;
  return body?.code || body?.error;
}

export default function SharePage() {
  const { shareToken } = useParams<{ shareToken: string }>();
  const [loading, setLoading] = useState(true);
  const [shareData, setShareData] = useState<ShareData | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [passwordForm] = Form.useForm<{ password: string }>();

  useEffect(() => {
    // Validate the share token
    const validateShare = async () => {
      if (!shareToken) {
        setShareData({ isValid: false, isExpired: false, requiresPassword: false });
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const shareInfo = await getPublicShare(shareToken);

        if (!shareInfo.valid) {
          // API verdict is authoritative; the local expiresAt comparison only
          // picks which result screen (expired vs not found) to show.
          const expiredHint = shareInfo.expiresAt
            ? new Date(shareInfo.expiresAt) < new Date()
            : false;
          setShareData({
            isValid: false,
            isExpired: expiredHint,
            requiresPassword: false,
          });
        } else {
          setShareData({
            isValid: true,
            isExpired: false,
            requiresPassword: !!shareInfo.requiresPassword,
            permissions: shareInfo.permissions,
            fileName: shareInfo.fileName,
            mimeType: shareInfo.mimeType,
            size: shareInfo.size,
          });
        }
      } catch (error) {
        console.error("Error validating share:", error);
        // 401 = invalid/expired token per the contract
        const status = axios.isAxiosError(error)
          ? error.response?.status
          : undefined;
        setShareData({
          isValid: false,
          isExpired: status === 401,
          requiresPassword: false,
        });
      } finally {
        setLoading(false);
      }
    };

    validateShare();
  }, [shareToken]);

  const handleDownload = async (password?: string) => {
    if (!shareToken) return;

    setDownloading(true);
    try {
      await downloadPublicShare(shareToken, password, shareData?.fileName);
    } catch (error) {
      const status = axios.isAxiosError(error)
        ? error.response?.status
        : undefined;
      const code = shareErrorCode(error);

      if (status === 403 && code === "ACCESS_DENIED") {
        message.error("Incorrect password");
        passwordForm.resetFields();
      } else if (status === 403 && code === "DOWNLOAD_NOT_PERMITTED") {
        message.error("This file is view-only and cannot be downloaded.");
      } else if (status === 401) {
        setShareData({ isValid: false, isExpired: true, requiresPassword: false });
      } else if (status === 404) {
        setShareData({ isValid: false, isExpired: false, requiresPassword: false });
      } else {
        message.error("Failed to download file");
      }
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#F8F7F5",
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  if (!shareData?.isValid) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#F8F7F5",
        }}
      >
        {shareData?.isExpired ? (
          <Result
            status="warning"
            title="Link expired"
            subTitle="This share link has expired and is no longer available."
          />
        ) : (
          <Result
            status="404"
            title="Link not found"
            subTitle="This share link is invalid or has been removed."
          />
        )}
      </div>
    );
  }

  const canDownload = shareData.permissions === "download";

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        background: "#F8F7F5",
        padding: 24,
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 32,
        }}
      >
        <img
          src="/secondary-logo-gradient.png"
          alt="AIXA"
          style={{ width: 32, height: 32, objectFit: "contain" }}
        />
        <Text strong style={{ fontSize: 18, color: "#0F6E56", letterSpacing: "0.5px" }}>
          AIXA <span style={{ fontWeight: 400 }}>Share</span>
        </Text>
      </div>

      <Card
        style={{
          maxWidth: 480,
          width: "100%",
          borderRadius: 12,
          boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
        }}
        styles={{
          body: { padding: 32 },
        }}
      >
        <div style={{ textAlign: "center" }}>
          {/* File preview */}
          <div
            style={{
              padding: 24,
              background: "#f5f5f5",
              borderRadius: 8,
              marginBottom: 24,
            }}
          >
            {shareData.mimeType ? (
              <FileIcon mimeType={shareData.mimeType} size="large" />
            ) : (
              <FileOutlined style={{ fontSize: 48, color: "#8c8c8c" }} />
            )}
          </div>

          {/* File info */}
          <Title level={4} style={{ marginBottom: 8, wordBreak: "break-word" }}>
            {shareData.fileName || "Unknown file"}
          </Title>

          {(shareData.size !== undefined || shareData.mimeType) && (
            <Space style={{ marginBottom: 24 }}>
              {shareData.size !== undefined && (
                <Text type="secondary">{formatFileSize(shareData.size)}</Text>
              )}
              {shareData.size !== undefined && shareData.mimeType && (
                <Text type="secondary">·</Text>
              )}
              {shareData.mimeType && (
                <Text type="secondary">
                  {shareData.mimeType.split("/")[1]?.toUpperCase()}
                </Text>
              )}
            </Space>
          )}

          {/* Actions */}
          {canDownload ? (
            shareData.requiresPassword ? (
              <>
                <Paragraph type="secondary" style={{ marginBottom: 16 }}>
                  <LockOutlined style={{ marginRight: 4 }} />
                  This file is password protected. Enter the password to
                  download it.
                </Paragraph>
                <Form
                  form={passwordForm}
                  onFinish={(values) => handleDownload(values.password)}
                >
                  <Form.Item
                    name="password"
                    rules={[
                      { required: true, message: "Please enter the password" },
                    ]}
                  >
                    <Input.Password
                      placeholder="Enter password"
                      size="large"
                      prefix={<LockOutlined />}
                    />
                  </Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    size="large"
                    icon={<DownloadOutlined />}
                    loading={downloading}
                    block
                  >
                    Download file
                  </Button>
                </Form>
              </>
            ) : (
              <Button
                type="primary"
                size="large"
                icon={<DownloadOutlined />}
                loading={downloading}
                onClick={() => handleDownload()}
                block
              >
                Download file
              </Button>
            )
          ) : (
            <Paragraph type="secondary">
              This file is view-only and cannot be downloaded.
            </Paragraph>
          )}

          {/* Security notice */}
          <div
            style={{
              marginTop: 24,
              padding: 12,
              background: "rgba(15, 110, 86, 0.04)",
              borderRadius: 8,
            }}
          >
            <Text type="secondary" style={{ fontSize: 12 }}>
              <LockOutlined style={{ marginRight: 4 }} />
              This file is shared securely through AIXA Share
            </Text>
          </div>
        </div>
      </Card>

      {/* Footer */}
      <Text
        type="secondary"
        style={{ marginTop: 32, fontSize: 12, textAlign: "center" }}
      >
        Shared files are encrypted and stored securely.
        <br />
        Access is logged for security purposes.
      </Text>
    </div>
  );
}
